/* Atlas v3 — The Brief reader sheet (mobile + desktop).

   Opens IN-APP when The Brief editorial block on the Overview is tapped.
   The Overview only shows the headline + first 1-2 sentences of the daily
   Atlas Intel briefing; the FULL long-form write-up (atlas_intel.body,
   written every weekday ~5:40 PM CT by Sonnet 4.6) is already in the client
   via useAtlasIntel() but had no reader until this. This renders the whole
   body as formatted editorial blocks.

   Mirrors NewsSheet's shell/motion: phone = slide-up bottom sheet, desktop =
   centered modal card (via the `desktop` prop). */
import { useState, useEffect, useMemo } from 'react'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Serif = { fontFamily: 'var(--studio-font-display)', fontWeight: 400 }
const Sans = { fontFamily: 'var(--studio-font-sans)' }

// Strip inline markdown emphasis so raw ** _ ` # never leak into the reader.
function clean(s) {
  return String(s || '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/[*_`]/g, '')
    .trim()
}

// Light markdown → block list. Handles headings (#/##/###), bullet lists
// (-/*/+), horizontal rules and blank-line paragraph breaks. Skips the
// "Atlas Intel — {date}" title line (the date lives in the eyebrow).
function parseBlocks(body) {
  const lines = String(body || '').replace(/\r/g, '').split('\n')
  const blocks = []
  let para = []
  const flush = () => { if (para.length) { blocks.push({ type: 'p', text: para.join(' ') }); para = [] } }
  for (const raw of lines) {
    const l = raw.trim()
    if (!l) { flush(); continue }
    if (/^#{0,3}\s*Atlas Intel\b/i.test(l)) { flush(); continue }         // title line — skip
    if (/^(?:---+|\*\*\*+|___+)$/.test(l)) { flush(); continue }          // hr — skip
    const h = l.match(/^(#{1,3})\s+(.*)$/)
    if (h) { flush(); blocks.push({ type: 'h', level: h[1].length, text: clean(h[2]) }); continue }
    const li = l.match(/^[-*+]\s+(.*)$/)
    if (li) { flush(); blocks.push({ type: 'li', text: clean(li[1]) }); continue }
    para.push(clean(l))
  }
  flush()
  return blocks
}

function Blocks({ blocks }) {
  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === 'h') {
          if (b.level >= 3) {
            return (
              <div key={i} style={{ ...Mono, fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'hsl(var(--studio-accent))', margin: i === 0 ? '0 0 10px' : '22px 0 10px' }}>{b.text}</div>
            )
          }
          return (
            <div key={i} style={{ ...Serif, fontSize: 19, lineHeight: 1.24, color: 'hsl(var(--studio-text))', margin: i === 0 ? '0 0 10px' : '24px 0 10px', textWrap: 'pretty' }}>{b.text}</div>
          )
        }
        if (b.type === 'li') {
          return (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', margin: '7px 0' }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'hsl(var(--studio-accent))', flexShrink: 0, transform: 'translateY(-2px)' }} />
              <span style={{ ...Sans, fontSize: 14, lineHeight: 1.6, color: 'hsl(var(--studio-text-muted))', textWrap: 'pretty' }}>{b.text}</span>
            </div>
          )
        }
        return (
          <p key={i} style={{ ...Sans, fontSize: 14.5, lineHeight: 1.72, color: 'hsl(var(--studio-text-muted))', margin: i === 0 ? 0 : '15px 0 0', textWrap: 'pretty' }}>{b.text}</p>
        )
      })}
    </>
  )
}

export function BriefSheet({ doc, dateLabel, onClose, desktop = false }) {
  const [shown, setShown] = useState(false)
  useEffect(() => { const a = requestAnimationFrame(() => setShown(true)); return () => cancelAnimationFrame(a) }, [])
  const blocks = useMemo(() => parseBlocks(doc?.body ?? doc?.preview), [doc])
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])
  if (!doc) return null

  const panelStyle = desktop
    ? { position: 'relative', width: 'min(680px, 94vw)', maxHeight: '86vh', margin: '7vh auto 0', display: 'flex', flexDirection: 'column',
        background: 'hsl(var(--studio-background))', border: '1px solid hsl(var(--studio-border))', borderTop: '2px solid hsl(var(--studio-accent) / 0.55)', borderRadius: 'var(--studio-radius-lg)',
        boxShadow: '0 32px 80px -24px hsl(var(--studio-overlay) / 0.9)',
        opacity: shown ? 1 : 0, transform: shown ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 220ms ease, transform 220ms var(--studio-ease)', overflow: 'hidden' }
    : { position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '90%', display: 'flex', flexDirection: 'column',
        background: 'hsl(var(--studio-background))', borderTop: '2px solid hsl(var(--studio-accent) / 0.55)', borderRadius: '16px 16px 0 0',
        boxShadow: '0 -16px 44px hsl(var(--studio-overlay) / 0.6)',
        transform: shown ? 'translateY(0)' : 'translateY(100%)', transition: 'transform 340ms var(--studio-ease)', overflow: 'hidden' }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 80 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'hsl(var(--studio-overlay) / 0.66)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', opacity: shown ? 1 : 0, transition: 'opacity 220ms ease' }} />
      <div style={panelStyle}>
        <div style={{ padding: desktop ? '16px 20px 0' : '9px 20px 0', flexShrink: 0 }}>
          {!desktop && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
              <span onClick={onClose} style={{ width: 38, height: 4, borderRadius: 999, background: 'hsl(var(--studio-border))', cursor: 'pointer' }} />
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.18em', color: 'hsl(var(--studio-accent))' }}>THE BRIEF{dateLabel ? ` · ${String(dateLabel).toUpperCase()}` : ''}</span>
            <button onClick={onClose} aria-label="Close" style={{ width: 30, height: 30, borderRadius: 'var(--studio-radius-sm)', cursor: 'pointer', background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', color: 'hsl(var(--studio-text-muted))', fontSize: 14 }}>✕</button>
          </div>
        </div>

        <div className="atlas-scroll" style={{ flex: 1, overflowY: 'auto', padding: '16px 22px 34px' }}>
          {blocks.length > 0
            ? <Blocks blocks={blocks} />
            : <p style={{ ...Sans, fontSize: 14, lineHeight: 1.7, color: 'hsl(var(--studio-text-subtle))', margin: 0 }}>The full briefing isn&rsquo;t available yet — it posts after the 5:40 PM CT cron.</p>}
          <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', textAlign: 'center', marginTop: 26, paddingTop: 14, borderTop: '1px solid hsl(var(--studio-border) / 0.6)' }}>
            WRITTEN BY ATLAS INTEL · AFTER THE CLOSE
          </div>
        </div>
      </div>
    </div>
  )
}
